import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useState } from 'react';
import { Film, ImageIcon, Loader2, Plus, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { RichTextEditor } from '../ui/rich-text-editor';
import { Switch } from '../ui/switch';
import { MediaPicker } from './MediaPicker';
import { CMS_ICONS, CMS_ICON_NAMES } from './icons';
import { cn } from '../../lib/utils';
import { cmsApi, cmsHomepageApi, type CmsContentValue, type CmsFieldSpec, type CmsHomepageEntity } from '../../lib/api/cms';
import { StandortFaecherField } from './StandortFaecherField';
import { StandortEntityToggleField, type EntityToggleValue } from './StandortEntityToggleField';
import { CollapsibleItemCard, ExpandCollapseAll } from './CollapsibleItemCard';
import { dropOpen, itemSummary, swapOpen } from './listCollapse';
import { toRichTextHtml } from './richTextValue';

/**
 * Select which central FAQs (CMS > FAQ) appear on this page when the
 * "Standard-FAQs übernehmen" toggle is off. The value is the list of selected
 * FAQ question strings (stable-enough key without a separate id column).
 */
const FaqSelectField: React.FC<{
  value: string[];
  onChange: (v: string[]) => void;
}> = ({ value, onChange }) => {
  const [faqs, setFaqs] = useState<Array<{ question: string }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selected = Array.isArray(value) ? value : [];

  useEffect(() => {
    let active = true;
    cmsApi
      .getContent('faq')
      .then((c) => {
        if (!active) return;
        const src = (c.published && Object.keys(c.published).length ? c.published : c.draft) || {};
        const list = Array.isArray((src as { faqs?: unknown }).faqs) ? (src as { faqs: unknown[] }).faqs : [];
        setFaqs(
          list
            .map((f) => ({ question: String((f as { question?: unknown })?.question ?? '').trim() }))
            .filter((f) => f.question),
        );
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Konnte nicht geladen werden'));
    return () => {
      active = false;
    };
  }, []);

  const toggle = (question: string, on: boolean) => {
    onChange(on ? [...selected.filter((q) => q !== question), question] : selected.filter((q) => q !== question));
  };

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!faqs) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Lädt …
      </div>
    );
  }
  if (faqs.length === 0) {
    return <p className="text-sm text-muted-foreground">Noch keine zentralen FAQs angelegt (CMS &gt; FAQ).</p>;
  }
  return (
    <div className="divide-y rounded-md border">
      {faqs.map((f) => (
        <label key={f.question} className="flex items-center gap-3 px-3 py-2">
          <Switch checked={selected.includes(f.question)} onCheckedChange={(on: boolean) => toggle(f.question, on)} />
          <span className="min-w-0 flex-1 truncate text-sm">{f.question}</span>
        </label>
      ))}
    </div>
  );
};

/** Optional editor context threaded to field widgets that need the entity id. */
export interface CmsEditorContext {
  /** Numeric standort id when editing a "standort/{id}" or "standort-fach/…" document. */
  standortId?: number;
  /** Numeric fach id when editing a "standort-fach/{sid}-{fid}" document. */
  fachId?: number;
}

type Scalar = string;
type Obj = Record<string, unknown>;

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

// --- image field -------------------------------------------------------------

const ImageField: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30">
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <Input
            placeholder="Bild-URL"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              <ImageIcon className="mr-2 h-4 w-4" /> Aus Bibliothek
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
                <X className="mr-1 h-4 w-4" /> Entfernen
              </Button>
            )}
          </div>
        </div>
      </div>
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => onChange(url)}
      />
    </div>
  );
};

// --- video field -------------------------------------------------------------

const VideoField: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30">
          {value ? (
            <video src={value} className="h-full w-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <Film className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <Input
            placeholder="Video-URL (HLS .m3u8)"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              <Film className="mr-2 h-4 w-4" /> Aus Bibliothek
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
                <X className="mr-1 h-4 w-4" /> Entfernen
              </Button>
            )}
          </div>
        </div>
      </div>
      <MediaPicker
        kind="video"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => onChange(url)}
      />
    </div>
  );
};

// --- icon field --------------------------------------------------------------

const IconField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {CMS_ICON_NAMES.map((name) => {
      const Icon = CMS_ICONS[name];
      const active = value === name;
      return (
        <button
          key={name}
          type="button"
          title={name}
          onClick={() => onChange(active ? '' : name)}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
            active
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-input text-muted-foreground hover:bg-muted',
          )}
        >
          <Icon className="h-4 w-4" />
        </button>
      );
    })}
  </div>
);

// --- scalar field ------------------------------------------------------------

const ScalarField: React.FC<{
  field: CmsFieldSpec;
  value: Scalar;
  onChange: (v: Scalar) => void;
  /** Inherited value shown as a native placeholder when the field is empty. */
  placeholder?: string;
}> = ({ field, value, onChange, placeholder }) => {
  switch (field.type) {
    case 'textarea':
      return <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} />;
    case 'code':
      return (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={8}
          spellCheck={false}
          className="font-mono text-xs"
        />
      );
    case 'richtext':
      return <RichTextEditor value={toRichTextHtml(value)} onChange={onChange} placeholder={placeholder} />;
    case 'image':
      return <ImageField value={value} onChange={onChange} />;
    case 'video':
      return <VideoField value={value} onChange={onChange} />;
    case 'icon':
      return <IconField value={value} onChange={onChange} />;
    case 'toggle':
      return (
        <div className="flex items-center gap-2">
          <Switch checked={value === 'true'} onCheckedChange={(v) => onChange(v ? 'true' : 'false')} />
          <span className="text-sm text-muted-foreground">{value === 'true' ? 'An' : 'Aus'}</span>
        </div>
      );
    case 'select':
      return (
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Auswählen…" />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    default:
      return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />;
  }
};

// --- list field --------------------------------------------------------------

const ListField: React.FC<{
  field: CmsFieldSpec;
  value: Obj[];
  onChange: (v: Obj[]) => void;
}> = ({ field, value, onChange }) => {
  const itemFields = field.item_fields || [];
  const items = Array.isArray(value) ? value : [];
  // Einträge starten zugeklappt; neu hinzugefügte gehen direkt auf.
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set<number>());

  const toggle = (idx: number, isOpen: boolean) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(idx);
      else next.delete(idx);
      return next;
    });

  const update = (idx: number, key: string, v: Scalar) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: v } : it));
    onChange(next);
  };
  const remove = (idx: number) => {
    setOpen((prev) => dropOpen(prev, idx));
    onChange(items.filter((_, i) => i !== idx));
  };
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setOpen((prev) => swapOpen(prev, idx, target));
    onChange(next);
  };
  const add = () => {
    setOpen((prev) => new Set(prev).add(items.length));
    onChange([...items, {}]);
  };

  const allOpen = items.length > 0 && open.size >= items.length;

  return (
    <div className="space-y-3">
      {items.length > 1 && (
        <div className="flex justify-end">
          <ExpandCollapseAll
            allOpen={allOpen}
            onToggle={() => setOpen(allOpen ? new Set<number>() : new Set(items.map((_, i) => i)))}
          />
        </div>
      )}
      {items.map((item, idx) => (
        <CollapsibleItemCard
          key={idx}
          index={idx}
          count={items.length}
          summary={itemSummary(item as Obj, itemFields)}
          open={open.has(idx)}
          onOpenChange={(isOpen) => toggle(idx, isOpen)}
          onMove={(dir) => move(idx, dir)}
          onRemove={() => remove(idx)}
        >
          {itemFields.map((sub) => (
            <div key={sub.key} className="space-y-1">
              <Label className="text-xs">{sub.label}</Label>
              <ScalarField
                field={sub}
                value={asString((item as Obj)[sub.key])}
                onChange={(v) => update(idx, sub.key, v)}
              />
            </div>
          ))}
        </CollapsibleItemCard>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="mr-2 h-4 w-4" /> Hinzufügen
      </Button>
    </div>
  );
};

// --- top-level editor --------------------------------------------------------

export interface SlotEditorProps {
  fields: CmsFieldSpec[];
  value: CmsContentValue;
  onChange: (next: CmsContentValue) => void;
  context?: CmsEditorContext;
  /** Inherited values (field key -> display string) shown as placeholder/hint. */
  inherited?: Record<string, string>;
}

const DEFAULT_SECTION = 'Weitere';

/** Group fields into ordered sections by their `section` tag (first-appearance order). */
function groupSections(fields: CmsFieldSpec[]): { name: string; fields: CmsFieldSpec[] }[] {
  const order: string[] = [];
  const map = new Map<string, CmsFieldSpec[]>();
  for (const f of fields) {
    const name = f.section || DEFAULT_SECTION;
    if (!map.has(name)) {
      map.set(name, []);
      order.push(name);
    }
    map.get(name)!.push(f);
  }
  return order.map((name) => ({ name, fields: map.get(name)! }));
}

/**
 * Lists all entities of `source` (standort | fach | partner) with a Switch that
 * flips their `show_on_homepage` flag directly via the entity API. The state
 * lives on the entity, not in the page content — so the section's own draft is
 * unaffected and changes take effect independently of publish.
 */
const HomepageToggles: React.FC<{ source: string }> = ({ source }) => {
  const [items, setItems] = useState<CmsHomepageEntity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    cmsHomepageApi
      .list(source)
      .then((rows) => alive && setItems(rows))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Konnte nicht geladen werden'));
    return () => {
      alive = false;
    };
  }, [source]);

  const toggle = async (entity: CmsHomepageEntity, next: boolean) => {
    setBusy(entity.id);
    setItems((prev) => prev?.map((i) => (i.id === entity.id ? { ...i, show_on_homepage: next } : i)) ?? prev);
    try {
      await cmsHomepageApi.setVisible(source, entity.id, next);
    } catch {
      // revert on failure
      setItems((prev) => prev?.map((i) => (i.id === entity.id ? { ...i, show_on_homepage: !next } : i)) ?? prev);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!items) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Lädt …
      </div>
    );
  }
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Einträge vorhanden.</p>;
  }

  return (
    <div className="divide-y rounded-md border">
      {items.map((entity) => (
        <div key={entity.id} className="flex items-center justify-between gap-3 px-3 py-2">
          <span className="truncate text-sm">{entity.name}</span>
          <Switch
            checked={entity.show_on_homepage}
            disabled={busy === entity.id}
            onCheckedChange={(v) => void toggle(entity, v)}
          />
        </div>
      ))}
    </div>
  );
};

/** Truncate a long inherited value for a compact hint line. */
const truncate = (v: string, n = 140): string => (v.length > n ? `${v.slice(0, n)}…` : v);

/** Renders a flat list of fields against a value object. Reused by the block editor. */
export const CmsFields: React.FC<{
  fields: CmsFieldSpec[];
  value: CmsContentValue;
  onChange: (next: CmsContentValue) => void;
  context?: CmsEditorContext;
  /** Inherited values (field key -> display string), shown as placeholder + hint. */
  inherited?: Record<string, string>;
}> = ({ fields, value, onChange, context, inherited }) => {
  const setField = (key: string, v: unknown) => onChange({ ...value, [key]: v });
  return (
    <div className="space-y-6">
      {fields.map((field) => {
        const inh = inherited?.[field.key];
        return (
        <div key={field.key} className="space-y-1.5">
          <Label className="text-sm font-medium">{field.label}</Label>
          {field.help && <p className="text-xs text-muted-foreground">{field.help}</p>}
          {inh ? (
            <p className="text-xs italic text-muted-foreground">
              Von der {getSubjectLabels().singular}-Seite: „{truncate(inh)}"
            </p>
          ) : null}
          {field.type === 'homepage_toggles' ? (
            <HomepageToggles source={field.source ?? 'standort'} />
          ) : field.type === 'standort_faecher' ? (
            context?.standortId ? (
              <StandortFaecherField
                standortId={context.standortId}
                value={(value[field.key] as number[]) || []}
                onChange={(v) => setField(field.key, v)}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                {getSubjectLabels().plural} erscheinen nach dem ersten Speichern des Standorts.
              </p>
            )
          ) : field.type === 'standort_entity_toggle' ? (
            context?.standortId ? (
              <StandortEntityToggleField
                source={field.source ?? 'partner'}
                standortId={context.standortId}
                fachId={context.fachId}
                value={value[field.key] as EntityToggleValue}
                onChange={(v) => setField(field.key, v)}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Einträge erscheinen nach dem ersten Speichern des Standorts.
              </p>
            )
          ) : field.type === 'faq_select' ? (
            <FaqSelectField
              value={(value[field.key] as string[]) || []}
              onChange={(v) => setField(field.key, v)}
            />
          ) : field.type === 'list' ? (
            <ListField
              field={field}
              value={(value[field.key] as Obj[]) || []}
              onChange={(v) => setField(field.key, v)}
            />
          ) : (
            <ScalarField
              field={field}
              value={asString(value[field.key])}
              onChange={(v) => setField(field.key, v)}
              placeholder={inh}
            />
          )}
        </div>
        );
      })}
    </div>
  );
};

export const SlotEditor: React.FC<SlotEditorProps> = ({ fields, value, onChange, context, inherited }) => {
  const sections = groupSections(fields);
  const [active, setActive] = useState<string>(sections[0]?.name ?? DEFAULT_SECTION);

  const current = sections.find((s) => s.name === active) ?? sections[0];

  // Single-section view with a jump bar keeps the form short instead of one long scroll.
  return (
    <div className="space-y-5">
      {sections.length > 1 && (
        <div className="flex flex-wrap gap-1 border-b pb-3">
          {sections.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => setActive(s.name)}
              className={cn(
                'rounded-full px-3 py-1 text-sm transition-colors',
                s.name === current?.name
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70',
              )}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <CmsFields fields={current?.fields ?? []} value={value} onChange={onChange} context={context} inherited={inherited} />
    </div>
  );
};

export default SlotEditor;
